import { createBreakpoints } from '@mui/system';
import {
  b1Custom,
  b2Custom,
  captionCustom,
  h1Custom,
  h2Custom,
  h3Custom,
  h4Custom,
  h5Custom,
  h6Custom,
  s1Custom,
  s2Custom,
} from './fontStyling';
import { repsonsiveBreakPoints } from '../helpers/constants';
import { PaletteMode } from '@mui/material';

const breakpoints = createBreakpoints({});

export const getDesignToken = (mode: PaletteMode) => ({
  breakpoints: repsonsiveBreakPoints,
  typography: {
    fontFamily: [
      'Nunito',
      'LexendDeca',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: h1Custom,
    h2: h2Custom,
    h3: h3Custom,
    h4: h4Custom,
    h5: h5Custom,
    h6: h6Custom,
    subtitle1: s1Custom,
    subtitle2: s2Custom,
    body1: b1Custom,
    body2: b2Custom,
    caption: captionCustom,
  },
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          //light mode values
          background: {
            default: '#E4F2F6',
            paper: '#fff',
          },
          primary: {
            main: '#0F1177', //primary-color
            light: '#E4F2F6', //backgrounud blue
            dark: '#dee1e8', //border color for mobile containers
          },
          secondary: {
            main: '#0379A0', //soviet-blue
            light: '#0379A0',
          },
          warning: {
            main: '#ba2343',
          },
          text: {
            primary: '#000000',
            secondary: '#4f5f71',
          },
          common: {
            white: '#d9e1e7', //grey outline of boxes.
          },
          action: {
            disabledBackground: 'lightgray',
            disabled: 'rgba(0, 0, 0, 0.38)',
          },
        }
      : {
          background: {
            default: 'black',
            paper: 'black',
          },
          primary: {
            main: '#fff', //primary-color
            light: '#E4F2F6', //backgrounud blue
            dark: '#dee1e8', //border color for mobile containers
          },
          secondary: {
            main: '#0379A0', //soviet-blue
            light: '#0379A0',
          },
          warning: {
            main: '#ba2343',
          },
          text: {
            primary: '#fff',
            secondary: '#4f5f71',
          },
          common: {
            white: '#dee1e8', //grey outline of boxes.
          },
          action: {
            disabledBackground: 'lightgray',
            disabled: 'rgba(0, 0, 0, 0.38)',
          },
        }),
  },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:before': {
            backgroundColor: 'unset !important',
          },
          [breakpoints.down('sm')]: {
            margin: '5px 0',
          },
          [breakpoints.up('sm')]: {
            margin: '10px 0',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          [breakpoints.down('sm')]: {
            minHeight: '15px',
          },
          [breakpoints.up('sm')]: {
            minHeight: '80px',
          },
        },
        content: {
          [breakpoints.down('sm')]: {
            margin: '3px 0',
          },
          [breakpoints.up('sm')]: {
            margin: '10px 0',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          [breakpoints.down('sm')]: {},
          [breakpoints.up('sm')]: {},
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'Nunito',
          padding: '14px 35px',
          borderRadius: '15px',
          textTransform: 'capitalize' as const,
          '@media (max-width: 350px)': {
            fontSize: '11px',
          },
          '@media (max-width: 400px)': {
            padding: '12px 8px',
            fontSize: '12px',
          },
          '@media (min-width: 1025px) and (max-width: 1440px)': {
            padding: '10px 15px',
            fontSize: '0.775rem',
          },
          '&:hover': {
            backgroundColor: '#0f1177c7',
            color: '#fff',
          },
        },
        ...(mode === 'light'
          ? {
              outlined: {
                border: `1px solid #0F1177`,
                borderRadius: '16px',
                backgroundColor: 'white',
                color: '#050505',
                fontWeight: 600,
                padding: '1em',
                '@media (min-width: 1025px) and (max-width: 1440px)': {
                  padding: '10px 15px',
                  fontSize: '0.775rem',
                },
                '&:hover': {
                  backgroundColor: '#69697a17',
                  color: '#0f1177',
                  border: '1px solid #0f1177',
                },
              },
              contained: {
                backgroundColor: '#0F1177',
                color: '#E4F2F6',
                fontWeight: 600,
                '&:hover': {},
              },
            }
          : {
              outlined: {
                border: '#fff',
                color: '#fff',
              },
              contained: {
                backgroundColor: '#0F1177',
                color: '#E4F2F6',
              },
            }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          checked: {
            color: 'pink !important',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          [breakpoints.down('md')]: {
            gridAutoRows: 'min-content',
            gap: '2rem',
            [breakpoints.down('sm')]: {
              gap: '1.25rem',
            },
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          height: '4.0rem',
          [breakpoints.down('md')]: {
            height: '2.25rem',
          },
          [breakpoints.up('md')]: {
            '@media (min-resolution: 1.5dppx)': {
              '@media (max-height: 675px)': {
                height: '3.5rem',
              },
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            padding: '3.5px 4px 7.5px 6px !important',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          [breakpoints.down('sm')]: {
            fontSize: '12px',
            padding: '4px 10px',
          },
          [breakpoints.up('md')]: {
            fontSize: '12px',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          lineHeight: 1,
          color: 'rgba(0, 0, 0, 0.6)',
          fontFamily: 'LexendDeca',
          fontWeight: 500,
          [breakpoints.down('sm')]: {
            fontSize: 15,
          },
          [breakpoints.up('sm')]: {},
          [breakpoints.up('md')]: {},
          [breakpoints.up('lg')]: {},
        },
        asterisk: {
          color: '#db3131',
          '&$error': {
            color: '#db3131',
          },
        },
      },
    },
  },
});
